import { useWidgetData } from "../Contexts/dashboardHooks";
import { resolveDateRange } from "../Utils/resolveDateRange";
import { formatSeriesLabel } from "../Processing/seriesLabel";
import { WidgetInfoPopoverContent, type WidgetInfoRow } from "../WidgetComponents/WidgetInfoPopover";
import type { PieChartComponentSpec, PieSeriesBuilderMisc } from "../Processing/types";

/** A slice shows its own window, or the first slice's when it inherits. The label comes
 *  from the shared resolver — see the note in Info/chart.tsx. */
function resolveSliceDate(misc: PieSeriesBuilderMisc, firstMisc: PieSeriesBuilderMisc | undefined): string {
  if (misc.inheritDateRange && firstMisc) return resolveSliceDate(firstMisc, undefined);
  return resolveDateRange(misc);
}

export function PieInfo({ id }: { id: string }) {
  const data = useWidgetData(id);
  if (!data || data.type !== "pie_chart") return null;
  const spec = data.spec;
  if (!spec.series.length) return null;

  const firstMisc = spec.series[0]?.series_builder_misc;

  const rows: WidgetInfoRow[] = spec.series.map((s) => {
    const misc = s.series_builder_misc;
    const label = s.displayNameOverride?.trim()
      ? s.displayNameOverride
      : formatSeriesLabel(s.displayName, s.metricAgg, s.metricIdentifierType, misc);
    const subjects = misc.selectedSubjects ?? [];
    const drills = misc.selectedDrills ?? [];
    // An empty athlete side means full team; drills narrow it (AND semantics).
    const isFullTeam = subjects.length === 0 || subjects.includes("full-team");
    const athleteCount = subjects.filter((v) => v !== "full-team").length;
    const athleteLabel = isFullTeam
      ? "Full Team"
      : `${athleteCount} athlete${athleteCount !== 1 ? "s" : ""}`;
    return {
      queryId: s.queryId,
      label,
      aggLabel: { avg: "Average", sum: "Sum", best: "Best" }[s.metricAgg] ?? s.metricAgg,
      dates: resolveSliceDate(misc, firstMisc),
      subjects: drills.length > 0
        ? `${athleteLabel} in ${drills.length} drill${drills.length !== 1 ? "s" : ""}`
        : isFullTeam ? "Full Team" : `${athleteLabel} selected`,
    };
  });

  return <WidgetInfoPopoverContent rows={rows} />;
}
